import React, { useState, useEffect } from 'react';
// V2 — carrega plan_items pra mostrar composição do kit no resumo
import { plansV2API } from '../../services/api/v2/plans';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Modal, Button } from 'react-bootstrap';
import { formatCurrency } from '../../utils/productPricing';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import {
  processAffiliateLink,
  getAffiliateCookie,
  debugAffiliateCookies,
  useAffiliateDetection,
  saveAffiliateProductCookie
} from '../../utils/affiliateCookies';
import { cepService } from '../../services/api/cep';
import ModernSwitch from '../../components/forms/ModernSwitch';
import { injectAllTrackingScripts, removeAllTrackingScripts } from '../../utils/trackingScripts';
import { API_URL, PAYMENTS_API_URL, PAYMENTS_API_KEY } from '../../config/api';
import './LandingCheckout.css';

const LandingCheckout = () => {
  const { productId, checkoutId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Estados do produto e checkout
  const [product, setProduct] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // V2 — composição do kit (plan_items com produto físico + quantidade)
  const [planItems, setPlanItems] = useState([]);
  
  // Estados do formulário
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    isWhatsApp: true
  });

  const [shipping, setShipping] = useState({
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    country: 'Brasil'
  });
  
  const [payment, setPayment] = useState({
    method: '', // Será definido quando os métodos de pagamento forem carregados
    installments: 1
  });

  const [creditCard, setCreditCard] = useState({
    number: '',
    holder: '',
    month: '',
    year: '',
    cvc: ''
  });
  
  // Estados de processamento
  const [processing, setProcessing] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState(null);
  
  // Estados para modal PIX
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState(null);
  
  // Estados para modal Boleto
  const [showBoletoModal, setShowBoletoModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  // Estados para modal de erro detalhado
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  
  // Estado específico para modal de erro de valor mínimo de boleto
  const [showBoletoMinValueModal, setShowBoletoMinValueModal] = useState(false);
  const [boletoErrorMessage, setBoletoErrorMessage] = useState('');
  
  // Estados para Pagar.me tokenizecard.js
  const [paymentToken, setPaymentToken] = useState(null);
  const [pagarmeLoaded, setPagarmeLoaded] = useState(false);
  const [pagarmeError, setPagarmeError] = useState(null);
  const [isSubmittingToPagarme, setIsSubmittingToPagarme] = useState(false);
  const [pagarmePublicKey, setPagarmePublicKey] = useState(null);
  
  // Estado do frete
  const [shippingCost, setShippingCost] = useState({
    cost: 0,
    freeShipping: false,
    loading: false
  });


  // Funções para máscaras
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    // Limitar a 11 dígitos (apenas CPF)
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    // Se tiver mais de 11, cortar e formatar apenas os primeiros 11
    const limitedNumbers = numbers.substring(0, 11);
    return limitedNumbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Função para buscar endereço por CEP
  const handleCEPLookup = async (cep) => {
    try {
      setCepLoading(true);
      setCepError(null);

      console.log('🔍 Buscando CEP:', cep);

      const result = await cepService.lookup(cep);

      if (result.success) {
        // Atualizar campos de endereço automaticamente
        setShipping(prev => ({
          ...prev,
          zipCode: result.data.zipCode,
          street: result.data.street,
          neighborhood: result.data.neighborhood,
          city: result.data.city,
          state: result.data.state,
          country: result.data.country
        }));

        console.log('✅ Endereço preenchido automaticamente:', result.data);
        
        // Calcular frete após encontrar o endereço
        await calculateShippingCost(result.data.zipCode);
      } else {
        setCepError(result.error);
        console.error('❌ Erro ao buscar CEP:', result.error);
      }
    } catch (error) {
      setCepError('Erro interno ao buscar CEP');
      console.error('❌ Erro interno:', error);
    } finally {
      setCepLoading(false);
    }
  };

  // Função para limpar erro de CEP
  const clearCepError = () => {
    setCepError(null);
  };


  // Função para calcular fretetrw
  const calculateShippingCost = async (destinationCep) => {
    try {
      setShippingCost(prev => ({ ...prev, loading: true }));
      
      // ⚠️ VALIDAÇÃO CRÍTICA: Produto DIGITAL não cobra frete!
      const shipping = product?.shipping;
      
      // Se NÃO tem dados de shipping, é um produto DIGITAL
      if (!shipping) {
        console.log('📱 PRODUTO DIGITAL detectado - Frete GRÁTIS (sem cobrança)');
        setShippingCost({
          cost: 0,
          freeShipping: true,
          loading: false
        });
        return;
      }
      
      console.log('📦 PRODUTO FÍSICO detectado - Calculando frete...');
      
      // Simular cálculo de frete baseado na distância
      // Em uma implementação real, você usaria uma API de frete como Correios, Melhor Envio, etc.
      
      // ✅ CONVERSÃO CORRETA: Usar dados REAIS da tabela product_shipping
      const productPriceCents = parseInt(product?.plans?.[0]?.price || 0); // Manter em centavos
      
      // Determinar valor do frete baseado no tipo de frete configurado
      let baseShippingCostCents = 1590; // Valor padrão R$ 15,90 em centavos
      
      if (shipping.shipping_type === 'fixed' && shipping.fixed_shipping_value) {
        baseShippingCostCents = parseInt(shipping.fixed_shipping_value);
      } else if (shipping.shipping_type === 'variable' && shipping.default_shipping_value) {
        baseShippingCostCents = parseInt(shipping.default_shipping_value);
      }
      
      // Para frete grátis, usar preço do produto como limite (simulado)
      const freeShippingLimitCents = shipping?.shipping_included_in_price ? 0 : productPriceCents;
      
      console.log('🚚 Calculando frete (valores em centavos):', {
        destinationCep,
        productPriceCents,
        freeShippingLimitCents,
        baseShippingCostCents,
        shippingType: shipping?.shipping_type,
        freePac: shipping?.free_pac,
        acceptsSedex: shipping?.accepts_sedex,
        productPriceFormatted: formatCurrency(productPriceCents),
        baseShippingFormatted: formatCurrency(baseShippingCostCents)
      });
      
      // ✅ LÓGICA DE FRETE BASEADA NAS CONFIGURAÇÕES REAIS
      let hasFreeShipping = false;
      let finalShippingCostCents = 0;
      
      // 1. Verificar se frete está incluído no preço
      if (shipping?.shipping_included_in_price) {
        hasFreeShipping = true;
        finalShippingCostCents = 0;
      }
      // 2. Verificar se é PAC grátis
      else if (shipping?.free_pac) {
        hasFreeShipping = true;
        finalShippingCostCents = 0;
      }
      // 3. Se não é grátis, calcular baseado no tipo
      else {
        if (shipping?.shipping_type === 'fixed') {
          // Frete fixo - usar valor fixo configurado
          finalShippingCostCents = baseShippingCostCents;
        } else {
          // Frete variável - calcular baseado na região (simulação)
          if (destinationCep.startsWith('01') || destinationCep.startsWith('02') || 
              destinationCep.startsWith('03') || destinationCep.startsWith('04') ||
              destinationCep.startsWith('05') || destinationCep.startsWith('06') ||
              destinationCep.startsWith('07') || destinationCep.startsWith('08') ||
              destinationCep.startsWith('09')) {
            // CEPs de São Paulo (capital) - frete mais barato
            finalShippingCostCents = Math.round(baseShippingCostCents * 0.8); // 20% mais barato
          }
          else if (destinationCep.startsWith('1')) {
            // CEPs do interior de SP
            finalShippingCostCents = Math.round(baseShippingCostCents * 1.2); // 20% mais caro
          }
          else {
            // Outras regiões
            finalShippingCostCents = Math.round(baseShippingCostCents * 1.5); // 50% mais caro
          }
        }
      }
      
      setShippingCost({
        cost: finalShippingCostCents, // ✅ Salvar em centavos
        freeShipping: hasFreeShipping,
        loading: false
      });
      
      console.log('✅ Frete calculado:', {
        costCents: finalShippingCostCents,
        costFormatted: formatCurrency(finalShippingCostCents),
        freeShipping: hasFreeShipping,
        shippingConfig: {
          type: shipping?.shipping_type,
          freePac: shipping?.free_pac,
          acceptsSedex: shipping?.accepts_sedex,
          includedInPrice: shipping?.shipping_included_in_price,
          defaultValue: shipping?.default_shipping_value,
          fixedValue: shipping?.fixed_shipping_value
        }
      });
      
    } catch (error) {
      console.error('❌ Erro ao calcular frete:', error);
      setShippingCost(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Estados de afiliado com cookies
  const [affiliateConfig, setAffiliateConfig] = useState(null);
  const [affiliateData, setAffiliateData] = useState(null);
  const { checkForAffiliate, clearAffiliate } = useAffiliateDetection();
  
  // Dados do afiliado (legacy - mantido para compatibilidade)
  const affiliateRef = searchParams.get('ref');
  const affiliateId = searchParams.get('affiliate_id');
  const utmSource = searchParams.get('utm_source');

  // Estado para métodos de pagamento do checkout
  const [checkoutMethods, setCheckoutMethods] = useState({
    credit_card: false,
    boleto: false,
    pix: false,
    after_pay: false
  });

  // Debug: Log quando checkoutMethods muda
  useEffect(() => {
    console.log('🔄 checkoutMethods mudou:', checkoutMethods);
  }, [checkoutMethods]);

  // Debug: Log quando payment.method muda
  useEffect(() => {
    console.log('💳 payment.method mudou:', payment.method);
  }, [payment.method]);
  
  // Timer de urgência (condicional)
  const [timeLeft, setTimeLeft] = useState(null);
  const [showTimer, setShowTimer] = useState(false);

  // Carregar dados do produto
  useEffect(() => {
    loadProduct();
  }, [productId]);

  // Carregar configuração de afiliados e processar cookies
  useEffect(() => {
    loadAffiliateConfig();
  }, [productId]);

  // Processar dados de afiliado após carregar configuração
  useEffect(() => {
    if (affiliateConfig) {
      processAffiliateData();
    }
  }, [affiliateConfig]);

  // V2 — Carrega composição do plano (plan_items) pra mostrar no resumo
  useEffect(() => {
    const planId = product?.plans?.[0]?.id;
    if (!planId) {
      setPlanItems([]);
      return;
    }
    let mounted = true;
    plansV2API.findById(planId)
      .then((res) => {
        if (!mounted) return;
        const items = res?.data?.plan?.items || res?.data?.items || [];
        setPlanItems(items);
      })
      .catch(() => {
        if (mounted) setPlanItems([]);
      });
    return () => { mounted = false; };
  }, [product?.plans]);

  // Injetar scripts de tracking/analytics e chat quando checkout for carregado
  useEffect(() => {
    if (!checkout) return;

    console.log('🎯 Checkout carregado - verificando scripts de tracking e chat...');
    
    // Injetar scripts de tracking/analytics
    if (checkout.pixel_config) {
      const pixelConfig = typeof checkout.pixel_config === 'string' 
        ? JSON.parse(checkout.pixel_config) 
        : checkout.pixel_config;

      console.log('📊 Pixel Config:', pixelConfig);

      // Injetar chat se configurado
      const chatCode = checkout.chat_configuration || '';
      
      // Injetar todos os scripts
      injectAllTrackingScripts(pixelConfig, chatCode);
      
      console.log('✅ Scripts de tracking/analytics/chat injetados!');
    }

    // Cleanup: remover scripts quando o componente desmontar
    return () => {
      console.log('🗑️ Limpando scripts de tracking...');
      removeAllTrackingScripts();
    };
  }, [checkout]);

  // Timer countdown (condicional)
  useEffect(() => {
    if (!showTimer || !timeLeft) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (!prev) return null;
        
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showTimer, timeLeft]);

  // ✅ POLLING AUTOMÁTICO: Verificar status do pagamento PIX a cada 5 segundos
  // O backend agora verifica em tempo real na API de Payments (evita CORS)
  useEffect(() => {
    if (!showPixModal || !pixData?.sale_id) return;

    console.log('🔄 Iniciando polling automático para venda #' + pixData.sale_id);
    if (pixData?.order_id) {
      console.log('📦 Order ID (Payments): ' + pixData.order_id);
    }

    const checkPaymentStatus = async () => {
      try {
        // ✅ Consultar backend - ele verifica em tempo real na API de Payments
        // Isso evita problemas de CORS ao chamar a API de Payments diretamente
        const response = await fetch(`${API_URL}/api/sales/${pixData.sale_id}/payment-status`);

        if (response.ok) {
          const result = await response.json();
          const status = result.data?.status;
          console.log(`🔍 [Polling] Status da venda #${pixData.sale_id}: ${status}`);

          if (status === 'paid') {
            console.log('✅ [Polling] Pagamento confirmado!');

            // Preparar dados para página de sucesso
            const orderData = {
              id: pixData?.sale_id || Date.now(),
              created_at: new Date().toISOString(),
              total_amount: pixData?.total || 100,
              payment_method: 'pix',
              payment_status: 'paid',
              sale_status: 'paid',
              customer: {
                name: customer.name,
                email: customer.email,
                phone: customer.phone,
                document: customer.document || ''
              },
              shipping_address: {
                address: `${shipping.street}, ${shipping.number}`,
                street: shipping.street,
                number: shipping.number,
                complement: shipping.complement,
                neighborhood: shipping.neighborhood,
                city: shipping.city,
                state: shipping.state,
                zipCode: shipping.zipCode
              },
              product: {
                name: product.name,
                image: product.image_url || product.image
              },
              payment_info: {
                installments: 1
              }
            };

            // Fechar modal e redirecionar
            setShowPixModal(false);
            navigate('/checkout/success', { state: { order: orderData } });
            return;
          }
        } else {
          console.warn(`⚠️ [Polling] Resposta não OK: ${response.status}`);
        }
      } catch (error) {
        console.error('❌ [Polling] Erro ao verificar pagamento:', error);
      }
    };

    // Verificar imediatamente
    checkPaymentStatus();

    // Configurar intervalo de 5 segundos para detecção rápida
    const pollingInterval = setInterval(checkPaymentStatus, 5000);

    // Cleanup: parar polling quando modal fechar
    return () => {
      console.log('🛑 Parando polling automático');
      clearInterval(pollingInterval);
    };
  }, [showPixModal, pixData?.sale_id, pixData?.order_id, customer, shipping, product, navigate]);

  // DETECTAR TOKEN NA URL (Pagar.me sempre redireciona automaticamente)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pagarmeToken = urlParams.get('pagarmetoken-0') || urlParams.get('pagarmetoken');
    
    // ⚠️ BLOQUEIO DE LOOP: Verificar se já processamos este token
    const processedToken = sessionStorage.getItem('processed-token');
    if (processedToken === pagarmeToken) {
      console.log('⏭️ Token já processado, ignorando...');
      return;
    }
    
    if (pagarmeToken && product && !loading && !processing) {
      console.log('🎯 Token detectado na URL - processando...');
      console.log('🔍 Token recebido:', pagarmeToken);
      
      // Marcar token como processado IMEDIATAMENTE
      sessionStorage.setItem('processed-token', pagarmeToken);
      
      // NÃO limpar a URL ainda - vamos manter para debug
      // urlParams.delete('pagarmetoken-0');
      // urlParams.delete('pagarmetoken');
      
      // Recuperar dados salvos
      const savedData = localStorage.getItem('checkout-form-data');
      console.log('🔍 DEBUG - Dados salvos encontrados:', !!savedData);
      
      if (savedData) {
        console.log('✅ Recuperando dados salvos...');
        const formData = JSON.parse(savedData);
        
        console.log('📋 Dados recuperados:', {
          customer: formData.customer.name,
          email: formData.customer.email,
          payment: formData.payment.method
        });
        
        // Processar compra IMEDIATAMENTE sem redirect
        setProcessing(true);
        
        // Processar diretamente
        (async () => {
          try {
            console.log('🚀 Processando pagamento com token:', pagarmeToken);
            
            // Recuperar dados adicionais
            const productId = localStorage.getItem('checkout-product-id');
            const totalAmount = parseInt(localStorage.getItem('checkout-total-amount') || '0');
            const affiliateInfo = JSON.parse(localStorage.getItem('checkout-affiliate-info') || '{}');
            
            console.log('📦 Dados da compra:', {
              productId,
              totalAmount,
              affiliateInfo
            });
            
            const saleData = {
              product_id: parseInt(productId),
              plan_id: product?.plans?.[0]?.id || null,
              customer_data: formData.customer,
              shipping_address: formData.shipping,
              affiliate_data: affiliateInfo,
              payment_method: 'credit_card',
              payment_data: {
                payment_token: pagarmeToken,
                card_token: pagarmeToken,
                installments: formData.payment.installments || 1
              },
              installments: formData.payment.installments || 1,
              total_amount: totalAmount,
              affiliate_id: affiliateInfo.affiliate_id || null,
              utm_source: affiliateInfo.utm_source || null
            };
            
            console.log('📤 Enviando para backend:', saleData);
            
            const response = await fetch(`${API_URL}/api/checkout/process`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(saleData)
            });
            
            console.log('📊 Response status:', response.status);
            
            if (!response.ok) {
              const errorText = await response.text();
              console.error('❌ Erro do backend (texto):', errorText);
              
              try {
                const errorData = JSON.parse(errorText);
                console.error('❌ Erro do backend (JSON):', errorData);
                
                // Exibir modal de erro detalhado
                setErrorDetails({
                  status: response.status,
                  message: errorData.message || 'Erro ao processar pagamento',
                  error: errorData.error || 'Erro desconhecido',
                  details: errorData.details || null,
                  fullError: errorData
                });
                setShowErrorModal(true);
                setProcessing(false);
                return; // Parar execução
              } catch (e) {
                // Se não for JSON, exibir erro de texto
                setErrorDetails({
                  status: response.status,
                  message: `Erro ${response.status}`,
                  error: errorText,
                  details: null,
                  fullError: { errorText }
                });
                setShowErrorModal(true);
                setProcessing(false);
                return; // Parar execução
              }
            }
            
            const result = await response.json();
            console.log('✅ Resposta do backend:', result);
            
            if (result.success) {
              console.log('🎉 Pagamento processado com sucesso!');
              
              // Limpar dados salvos
              localStorage.removeItem('checkout-form-data');
              localStorage.removeItem('checkout-product-id');
              localStorage.removeItem('checkout-checkout-id');
              localStorage.removeItem('checkout-affiliate-info');
              localStorage.removeItem('checkout-total-amount');
              
              // Redirecionar para sucesso
              navigate('/checkout/success', {
                state: {
                  order: result.data,
                  payment_info: result.data.payment_info
                }
              });
            } else {
              console.error('❌ Erro:', result.message);
              setError(result.message || 'Erro ao processar pagamento');
              setProcessing(false);
            }
            
          } catch (error) {
            console.error('❌ ERRO CRÍTICO:', error);
            console.error('❌ Stack:', error.stack);
            setError(error.message || 'Erro ao processar pagamento');
            setProcessing(false);
          }
        })();
        
      } else {
        console.log('❌ DADOS NÃO ENCONTRADOS NO LOCALSTORAGE!');
        setError('Dados do formulário perdidos. Preencha novamente e tente outra vez.');
        setProcessing(false);
      }
    }
  }, [product, loading, processing, navigate]);

  // Carregar tokenizecard.js da Pagar.me
  useEffect(() => {
    const loadPagarmeScript = async () => {
      console.log('🔄 Carregando tokenizecard.js...');
      
      // Verificar se já foi carregado
      if (window.PagarmeCheckout) {
        console.log('✅ tokenizecard.js já carregado');
        setPagarmeLoaded(true);
        return;
      }

      try {
        // Buscar chave pública do backend
        console.log('🔑 Buscando chave pública da Pagar.me...');
        const keyResponse = await fetch(`${API_URL}/api/payments/public-key`);
        
        if (!keyResponse.ok) {
          throw new Error('Não foi possível obter chave pública da Pagar.me');
        }
        
        const keyData = await keyResponse.json();
        const publicKey = keyData.public_key;
        
        if (!publicKey) {
          throw new Error('Chave pública não encontrada');
        }
        
        setPagarmePublicKey(publicKey); // Salvar no estado
        
            console.log('✅ Chave pública obtida:', publicKey.substring(0, 10) + '...');
            
            // REMOVIDO: Alert de teste com cartões válidos
            console.log('📋 CARTÕES DE TESTE VÁLIDOS:');
            console.log('✅ APROVADO: 4000000000000010');
            console.log('❌ NEGADO: 4000000000000028'); 
            console.log('🔄 PROCESSANDO: 4000000000000036');

        const script = document.createElement('script');
        script.src = 'https://checkout.pagar.me/v1/tokenizecard.js';
        script.setAttribute('data-pagarmecheckout-app-id', publicKey);
        
        // ✅ CONFIGURAR URL DE RETORNO APÓS TOKENIZAÇÃO
        // Usar a URL atual do checkout para o Pagar.me retornar
        const postbackUrl = window.location.href.split('?')[0].split('#')[0];
        script.setAttribute('data-pagarmecheckout-postback-url', postbackUrl);
        console.log('🔗 Postback URL configurada:', postbackUrl);
        console.log('⚠️ Pagar.me vai retornar para a mesma página com token na URL');
      
      script.onload = () => {
        console.log('✅ tokenizecard.js carregado com sucesso');
        setPagarmeLoaded(true);
        setPagarmeError(null);
        
        // Inicializar tokenização automaticamente (Pagar.me detecta submit do form)
        console.log('✅ PagarmeCheckout disponível:', !!window.PagarmeCheckout);
        console.log('✅ PagarmeCheckout.init disponível:', !!(window.PagarmeCheckout && window.PagarmeCheckout.init));
        
        if (window.PagarmeCheckout && window.PagarmeCheckout.init) {
          console.log('🎯 PagarmeCheckout carregado, mas init será chamado apenas quando necessário');
          console.log('🔍 DEBUG - Funções disponíveis no PagarmeCheckout:', Object.keys(window.PagarmeCheckout));
          
          // NÃO inicializar automaticamente para evitar conflitos
          // O init será chamado apenas no handleFormSubmit
        }
      };
      
        script.onerror = () => {
          console.error('❌ Erro ao carregar tokenizecard.js');
          setPagarmeError('Erro ao carregar sistema de pagamento. Verifique sua conexão.');
          setPagarmeLoaded(false);
        };
        
        document.body.appendChild(script);
        
      } catch (error) {
        console.error('❌ Erro ao obter chave pública:', error);
        setPagarmeError(`Erro ao configurar pagamento: ${error.message}`);
        setPagarmeLoaded(false);
        
        // REMOVIDO: Alert de erro - usar apenas console.log
        console.error('❌ ERRO: Não conseguiu obter chave pública da Pagar.me! Detalhes:', error.message);
      }
    };
    
    // Carregar apenas se estivermos no ambiente correto
    if (typeof window !== 'undefined') {
      loadPagarmeScript();
    }
    
    return () => {
      // Cleanup se necessário
      const script = document.querySelector('script[src*="tokenizecard.js"]');
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, []); // Executar apenas uma vez

  const loadProduct = async () => {
    try {
      const response = await fetch(`${API_URL}/api/store/products/${productId}`);
      if (!response.ok) {
        throw new Error('Produto não encontrado');
      }
      
      const result = await response.json();
      if (result.success && result.product) {
        console.log('🔍 Produto carregado:', {
          id: result.product.id,
          name: result.product.name,
          image_url: result.product.image_url,
          image: result.product.image,
          plans: result.product.plans,
          allFields: Object.keys(result.product)
        });
        setProduct(result.product);
        
        // Buscar dados do checkout do produto
        try {
          const checkoutResponse = await fetch(`${API_URL}/api/products/${productId}/checkouts`);
          if (checkoutResponse.ok) {
            const checkoutData = await checkoutResponse.json();
            if (checkoutData.success && checkoutData.data && checkoutData.data.length > 0) {
              
              let activeCheckout;
              
              // Se checkoutId foi especificado na URL, buscar esse checkout específico
              // Aceita tanto ID numerico quanto checkout_code (formato chk_xxxxx)
              if (checkoutId) {
                activeCheckout = checkoutData.data.find(c => {
                  if (c.status !== 'active') return false;
                  // Tentar por checkout_code primeiro (formato: chk_xxxxx)
                  if (c.checkout_code && c.checkout_code === checkoutId) return true;
                  // Fallback para ID numerico
                  if (c.id === parseInt(checkoutId)) return true;
                  return false;
                });

                if (!activeCheckout) {
                  throw new Error(`Checkout ${checkoutId} não encontrado ou inativo`);
                }

                console.log(`✅ Usando checkout específico ${checkoutId}:`, activeCheckout.name);
              } else {
                // Senão, usar o primeiro checkout ativo (comportamento padrão)
                activeCheckout = checkoutData.data.find(c => c.status === 'active');
                
                if (!activeCheckout) {
                  throw new Error('Checkout não disponível no momento');
                }
                
                console.log('✅ Usando checkout padrão:', activeCheckout.name);
              }

              // Verificar se checkout está ativo
              if (activeCheckout.status !== 'active') {
                throw new Error('Este checkout não está mais disponível');
              }

              // Salvar dados do checkout
              setCheckout(activeCheckout);

              // Configurar timer se habilitado
              if (activeCheckout.use_counter && activeCheckout.counter_config?.enabled) {
                const timeLimit = activeCheckout.counter_config.time_limit || 3600; // padrão 1 hora
                const hours = Math.floor(timeLimit / 3600);
                const minutes = Math.floor((timeLimit % 3600) / 60);
                const seconds = timeLimit % 60;
                
                setTimeLeft({ hours, minutes, seconds });
                setShowTimer(true);
                console.log('⏰ Timer configurado:', { hours, minutes, seconds });
              } else {
                setShowTimer(false);
                console.log('⏰ Timer desabilitado');
              }

              // Parse payment methods se for string
              let paymentMethods = activeCheckout.payment_methods;
              console.log('🔍 Payment methods raw:', {
                type: typeof paymentMethods,
                value: paymentMethods,
                isString: typeof paymentMethods === 'string',
                fullObject: paymentMethods
              });
              
              if (typeof paymentMethods === 'string') {
                try {
                  paymentMethods = JSON.parse(paymentMethods);
                  console.log('✅ Payment methods parsed:', paymentMethods);
                } catch (e) {
                  console.error('❌ Erro ao fazer parse:', e);
                  paymentMethods = {};
                }
              }
              
              const methodsConfig = {
                credit_card: paymentMethods?.credit_card === true || paymentMethods?.cartao === true,
                boleto: paymentMethods?.boleto === true,
                pix: paymentMethods?.pix === true,
                after_pay: paymentMethods?.after_pay === true
              };
              
              console.log('💳 Configurando métodos de pagamento:', {
                rawPaymentMethods: activeCheckout.payment_methods,
                parsedPaymentMethods: paymentMethods,
                finalConfig: methodsConfig,
                checkoutId: activeCheckout.id,
                checkoutName: activeCheckout.name
              });
              
              setCheckoutMethods(methodsConfig);
              
              // Definir o primeiro método disponível como padrão
              const availableMethods = Object.keys(methodsConfig).filter(key => methodsConfig[key]);
              if (availableMethods.length > 0 && !payment.method) {
                const defaultMethod = availableMethods[0];
                console.log('💳 Definindo método padrão:', defaultMethod);
                setPayment(prev => ({ ...prev, method: defaultMethod }));
              }

              // Se o checkout tem plano específico, filtrar pelo plano correto
              if (activeCheckout.linked_plan_id && result.product.plans) {
                const specificPlan = result.product.plans.find(plan => 
                  plan.id === activeCheckout.linked_plan_id
                );
                
                if (specificPlan) {
                  // Usar apenas o plano específico do checkout
                  setProduct(prev => ({
                    ...prev,
                    plans: [specificPlan]
                  }));
                  console.log('✅ Usando plano específico do checkout:', specificPlan.name);
                } else {
                  console.log('⚠️ Plano específico não encontrado, usando plano padrão');
                }
              }
            } else {
              // ⚡ CRIAR CHECKOUT PADRÃO AUTOMATICAMENTE
              console.log('⚠️ Nenhum checkout encontrado - criando checkout padrão...');
              
              try {
                const createResponse = await fetch(`${API_URL}/api/products/${productId}/checkouts`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    name: `Checkout Padrão - ${result.product.name}`,
                    description: 'Checkout criado automaticamente',
                    payment_methods: {
                      credit_card: true,
                      pix: true,
                      boleto: true,
                      after_pay: false
                    },
                    require_phone: true,
                    require_cpf: true,
                    require_address: true,
                    status: 'active'
                  })
                });
                
                if (createResponse.ok) {
                  const createData = await createResponse.json();
                  console.log('✅ Checkout padrão criado:', createData.data);
                  
                  // Usar o checkout recém-criado
                  const newCheckout = createData.data;
                  setCheckout(newCheckout);
                  
                  // Configurar métodos de pagamento
                  const methodsConfig = {
                    credit_card: true,
                    boleto: true,
                    pix: true,
                    after_pay: false
                  };
                  
                  setCheckoutMethods(methodsConfig);
                  
                  // Definir método padrão
                  if (!payment.method) {
                    setPayment(prev => ({ ...prev, method: 'credit_card' }));
                  }
                  
                  console.log('✅ Checkout padrão configurado e pronto para uso');
                } else {
                  throw new Error('Não foi possível criar checkout padrão');
                }
              } catch (createErr) {
                console.error('❌ Erro ao criar checkout padrão:', createErr);
                throw new Error('Nenhum checkout configurado para este produto');
              }
            }
          } else {
            throw new Error('Erro ao carregar configurações do checkout');
          }
        } catch (checkoutErr) {
          throw new Error(checkoutErr.message || 'Erro ao carregar checkout');
        }
      } else {
        throw new Error(result.message || 'Produto não encontrado');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAffiliateConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products/${productId}/affiliate-config`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAffiliateConfig(result.data);
          console.log('🔧 Configuração de afiliado carregada:', result.data);
        }
      } else {
        // Se a rota não existe ou retorna erro, usar configuração padrão
        console.warn('⚠️ Usando configuração de afiliado padrão');
        setAffiliateConfig({
          cookie_duration: 30,
          commission_value: 0,
          auto_approval: false
        });
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configuração de afiliado:', error);
      // Usar configuração padrão se falhar
      setAffiliateConfig({
        cookie_duration: 30,
        commission_value: 0,
        auto_approval: false
      });
    }
  };

  const processAffiliateData = () => {
    try {
      // Processar link de afiliado com duração específica do produto
      const cookieDuration = affiliateConfig?.cookie_duration || 30;
      const cookieDurationHours = cookieDuration * 24; // Converter dias para horas
      const processedData = processAffiliateLink(cookieDuration);

      if (processedData) {
        setAffiliateData(processedData);
        console.log('🍪 Dados de afiliado processados:', {
          duration: cookieDuration,
          data: processedData
        });

        // Salvar também o cookie específico do produto (aff_{productId})
        // Este é o formato que o backend espera para detectar o afiliado na venda
        const affCode = processedData.affiliate_code || processedData.link_code || processedData.aff;
        if (affCode && productId) {
          // Obter o ID numérico do produto se for product_code
          const numericProductId = product?.id || productId;
          saveAffiliateProductCookie(affCode, numericProductId, cookieDurationHours, processedData.affiliate_id || null);
          console.log('🍪 Cookie específico do produto salvo:', {
            productId: numericProductId,
            affCode,
            duration: `${cookieDurationHours}h`
          });
        }

        // Debug para desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          debugAffiliateCookies();
        }
      } else {
        console.log('🔗 Nenhum dado de afiliado detectado');
      }
    } catch (error) {
      console.error('❌ Erro ao processar dados de afiliado:', error);
    }
  };

  // Handler para submit do form (Pagar.me detecta automaticamente)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Evitar loop infinito
    if (isSubmittingToPagarme) {
      console.log('🔄 Já está processando com Pagar.me, ignorando novo submit...');
      return;
    }
    
    // Se for cartão de crédito, deixar o Pagar.me processar primeiro
    if (payment.method === 'credit_card') {
      console.log('💳 Cartão de crédito detectado - aguardando tokenização da Pagar.me...');
      
      // Marcar como processando para evitar loop
      setIsSubmittingToPagarme(true);
      
      // DEBUG: Verificar se os campos estão preenchidos
      const cardNumber = document.querySelector('[data-pagarmecheckout-element="number"]')?.value;
      const holderName = document.querySelector('[data-pagarmecheckout-element="holder_name"]')?.value;
      const expMonth = document.querySelector('[data-pagarmecheckout-element="exp_month"]')?.value;
      const expYear = document.querySelector('[data-pagarmecheckout-element="exp_year"]')?.value;
      const cvv = document.querySelector('[data-pagarmecheckout-element="cvv"]')?.value;
      
      console.log('🔍 DEBUG - Dados do cartão:', {
        cardNumber: cardNumber ? cardNumber.substring(0, 4) + '****' : 'VAZIO',
        holderName: holderName || 'VAZIO',
        expMonth: expMonth || 'VAZIO',
        expYear: expYear || 'VAZIO',
        cvv: cvv ? '***' : 'VAZIO'
      });
      
      if (!cardNumber || !holderName || !expMonth || !expYear || !cvv) {
        console.error('❌ ERRO: Campos do cartão estão vazios! Preencha todos os dados do cartão antes de finalizar.');
        setIsSubmittingToPagarme(false); // Reset flag
        return;
      }
      
      // PASSO 1: USAR TOKENIZECARD.JS DE FORMA SIMPLES
      console.log('🎯 PASSO 1: Usando tokenizecard.js para gerar token...');
      console.log('📋 Dados capturados:', {
        cardNumber: cardNumber ? cardNumber.substring(0, 4) + '****' + cardNumber.slice(-4) : 'VAZIO',
        holderName: holderName || 'VAZIO',
        expMonth: expMonth || 'VAZIO',
        expYear: expYear || 'VAZIO',
        cvv: cvv ? '***' : 'VAZIO'
      });
      
      // Calcular dados necessários antes de salvar
      const productPrice = product?.plans?.[0]?.price || 0;
      const totalAmount = parseInt(parseFloat(productPrice)); // Preço já vem em centavos
      
      const affiliateInfo = {
        affiliate_id: affiliateId || null,
        ref: null,
        utm_source: utmSource || null,
        utm_medium: null,
        utm_campaign: null,
        utm_term: null,
        utm_content: null
      };
      
      // SALVAR dados antes do Pagar.me redirecionar
      const formDataToSave = { customer, shipping, payment };
      localStorage.setItem('checkout-form-data', JSON.stringify(formDataToSave));
      
      // Salvar dados adicionais necessários para processar o pagamento
      // IMPORTANTE: Salvar o ID numérico do produto, não o productCode da URL
      localStorage.setItem('checkout-product-id', product?.id?.toString() || '');
      localStorage.setItem('checkout-checkout-id', checkoutId || '');
      localStorage.setItem('checkout-affiliate-info', JSON.stringify(affiliateInfo));
      localStorage.setItem('checkout-total-amount', totalAmount.toString());
      
      console.log('💾 Dados salvos no localStorage para processamento posterior');
      
      // Verificar se PagarmeCheckout está disponível
      if (!window.PagarmeCheckout) {
        console.error('❌ ERRO: PagarmeCheckout não está disponível!');
        setIsSubmittingToPagarme(false);
        return;
      }
      
      console.log('🔄 Usando PagarmeCheckout.init() para tokenizar...');
      
      try {
        // Usar o PagarmeCheckout.init() que já sabemos que funciona
        const tokenData = await new Promise((resolve, reject) => {
          // Timeout de segurança
          const timeout = setTimeout(() => {
            reject(new Error('Timeout na tokenização'));
          }, 10000);
          
          console.log('🎯 Chamando PagarmeCheckout.init() para tokenização...');
          
          // Primeiro, vamos verificar se os campos estão sendo detectados pelo Pagar.me
          const numberField = document.querySelector('[data-pagarmecheckout-element="number"]');
          const holderField = document.querySelector('[data-pagarmecheckout-element="holder_name"]');
          const monthField = document.querySelector('[data-pagarmecheckout-element="exp_month"]');
          const yearField = document.querySelector('[data-pagarmecheckout-element="exp_year"]');
          const cvvField = document.querySelector('[data-pagarmecheckout-element="cvv"]');
          
          console.log('🔍 DEBUG - Campos encontrados:', {
            numberField: !!numberField,
            holderField: !!holderField,
            monthField: !!monthField,
            yearField: !!yearField,
            cvvField: !!cvvField
          });
          
          console.log('🔍 DEBUG - Valores dos campos:', {
            number: numberField?.value || 'VAZIO',
            holder: holderField?.value || 'VAZIO',
            month: monthField?.value || 'VAZIO',
            year: yearField?.value || 'VAZIO',
            cvv: cvvField?.value || 'VAZIO'
          });
          
          // Usar o init que já está funcionando
          if (window.PagarmeCheckout && window.PagarmeCheckout.init) {
            console.log('🔄 Inicializando PagarmeCheckout com novos callbacks...');
            
            window.PagarmeCheckout.init(
              function success(data) {
                console.log('✅ Token gerado - Pagar.me vai redirecionar automaticamente');
                clearTimeout(timeout);
                // Deixar o Pagar.me redirecionar - vamos capturar na URL
                resolve(data);
              },
              function fail(error) {
                console.error('❌ Erro na tokenização:', error);
                clearTimeout(timeout);
                reject(error);
              }
            );
            
            console.log('✅ PagarmeCheckout.init() configurado com sucesso');
            
            // Simular um submit do formulário para acionar o init
            setTimeout(() => {
              const form = document.querySelector('[data-pagarmecheckout-form]');
              if (form) {
                console.log('🚀 Simulando submit para acionar tokenização...');
                console.log('🔍 Form encontrado:', !!form);
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                console.log('🔍 Evento criado:', submitEvent);
                const result = form.dispatchEvent(submitEvent);
                console.log('🔍 Resultado do dispatch:', result);
              } else {
                console.error('❌ Formulário [data-pagarmecheckout-form] não encontrado!');
                clearTimeout(timeout);
                reject(new Error('Formulário não encontrado'));
              }
            }, 500); // Aumentar delay para 500ms
            
          } else {
            clearTimeout(timeout);
            reject(new Error('PagarmeCheckout.init não está disponível'));
          }
        });
        
        console.log('✅ Pagar.me inicializado - aguardando redirecionamento automático');
        setIsSubmittingToPagarme(false);
        // Não fazer nada aqui - o Pagar.me vai redirecionar automaticamente
        // e vamos capturar o token na URL
        
      } catch (error) {
        console.error('❌ ERRO NA TOKENIZAÇÃO:', error);
        console.error('❌ ERRO:', error.message);
        setIsSubmittingToPagarme(false);
      }
      
      return; // Interromper o fluxo aqui
    }
    
    // Para outros métodos de pagamento, processar normalmente
    processPurchase();
  };

  // Função para processar compra com dados específicos (evita problemas de timing dos estados)
  const processPurchaseWithData = async (tokenFromPagarme, formData) => {
    try {
      console.log('🚀 INICIANDO processPurchaseWithData com dados diretos...');
      setProcessing(true);
      
      const customerData = formData.customer;
      const shippingData = formData.shipping;
      const paymentData = formData.payment;
      
      console.log('🔍 DEBUG - Dados do cliente (diretos):', customerData);
      console.log('🔍 DEBUG - Dados de entrega (diretos):', shippingData);
      console.log('🔍 DEBUG - Dados de pagamento (diretos):', paymentData);
      console.log('🔍 DEBUG - Token:', tokenFromPagarme);
      
      const finalToken = tokenFromPagarme || paymentToken;
      console.log('🎯 DEBUG - Token final a ser usado:', finalToken);
      
      const errors = [];
      
      // Validações usando dados diretos
      if (!customerData.name) errors.push('Nome completo');
      if (!customerData.email) errors.push('E-mail');
      if (!customerData.phone) errors.push('Celular');
      if (!customerData.document) errors.push('CPF');
      
      // ⚠️ Endereço obrigatório APENAS para produtos FÍSICOS
      const isPhysicalProduct = product?.shipping !== null && product?.shipping !== undefined;
      
      if (isPhysicalProduct) {
        if (!shippingData.zipCode) errors.push('CEP');
        if (!shippingData.street) errors.push('Rua/Logradouro');
        if (!shippingData.number) errors.push('Número');
        if (!shippingData.neighborhood) errors.push('Bairro');
        if (!shippingData.city) errors.push('Cidade');
        if (!shippingData.state) errors.push('Estado');
      } else {
        console.log('📱 Produto DIGITAL (processPurchaseWithData) - Validações de endereço IGNORADAS');
      }
      
      if (paymentData.method === 'credit_card' && !finalToken) {
        errors.push('Token de pagamento é obrigatório para cartão de crédito');
      }
      
      if (errors.length > 0) {
        throw new Error(`Preencha ou corrija os seguintes campos: ${errors.join(', ')}`);
      }
      
      // Calcular dados necessários - PREÇO JÁ VEM EM CENTAVOS!
      const productPrice = product?.plans?.[0]?.price || 0;
      const shippingValue = shippingCost.freeShipping ? 0 : (shippingCost.cost || 0);
      const totalAmount = parseInt(parseFloat(productPrice)) + shippingValue; // Produto + Frete
      
      const affiliateInfo = {
        affiliate_id: affiliateId || null,
        ref: null,
        utm_source: utmSource || null
      };
      
      const saleData = {
        product_id: product?.id, // Usar ID numérico do produto carregado (nunca parseInt do productCode)
        plan_id: product?.plans?.[0]?.id || null,
        customer_data: customerData,
        shipping_address: shippingData,
        affiliate_data: affiliateInfo,
        payment_method: paymentData.method,
        payment_data: paymentData.method === 'credit_card' ? {
          payment_token: finalToken,
          card_token: finalToken, // Enviar ambos para compatibilidade
          installments: paymentData.installments || 1
        } : null,
        installments: paymentData.installments || 1,
        total_amount: totalAmount,
        affiliate_id: affiliateId || null,
        utm_source: utmSource || null
      };
      
      console.log('🚀 Enviando dados para checkout com dados diretos...');
      
      const response = await fetch(`${API_URL}/api/checkout/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao processar compra');
      }
      
      const responseData = await response.json();
      console.log('🎉 VENDA PROCESSADA COM SUCESSO!', responseData);
      
      // Salvar dados da venda no localStorage para a página de sucesso
      const saleInfo = {
        id: responseData.data?.id || Date.now(),
        total: (responseData.data?.total_amount || saleData.total_amount) / 100,
        email: customerData.email,
        customer_name: customerData.name,
        payment_method: paymentData.method,
        date: new Date().toISOString()
      };
      localStorage.setItem('sale-success-data', JSON.stringify(saleInfo));
      
      // Limpar dados salvos e redirecionar
      localStorage.removeItem('checkout-form-data');
      window.location.href = '/#/checkout/success';
      
    } catch (err) {
      console.error('❌ ERRO em processPurchaseWithData:', err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ✅ VALIDAÇÃO IMEDIATA AO CLICAR EM BOLETO
  const handleBoletoClick = () => {
    // Pegar o preço do produto (já vem em centavos do backend)
    const rawPrice = product?.plans?.[0]?.price || 0;
    const priceInCents = parseInt(parseFloat(rawPrice));
    const BOLETO_MINIMUM_VALUE = 1000; // R$ 10,00 em centavos

    console.log('🎯 Clique em Boleto - Verificando valor:', {
      priceInCents,
      priceFormatted: CurrencyFormatter.centsToFormatted(priceInCents),
      minimumRequired: CurrencyFormatter.centsToFormatted(BOLETO_MINIMUM_VALUE),
      isValid: priceInCents >= BOLETO_MINIMUM_VALUE
    });

    // Se valor for menor que R$ 10,00, mostrar erro E DESSELECIONAR BOLETO
    if (priceInCents < BOLETO_MINIMUM_VALUE) {
      console.warn('⚠️ Valor abaixo do mínimo para boleto - BLOQUEANDO SELEÇÃO');
      
      // FORÇAR desselecionar boleto (voltar para vazio ou PIX se disponível)
      const fallbackMethod = checkoutMethods.pix ? 'pix' : 
                           checkoutMethods.credit_card ? 'credit_card' : '';
      
      setPayment(prev => ({ ...prev, method: fallbackMethod }));
      
      // Usar estado separado para modal de boleto (não misturar com modal de erro genérico)
      setBoletoErrorMessage(`O valor da compra (${CurrencyFormatter.centsToFormatted(priceInCents)}) está abaixo do valor mínimo de ${CurrencyFormatter.centsToFormatted(BOLETO_MINIMUM_VALUE)} para pagamentos via boleto.`);
      setShowBoletoMinValueModal(true);
      
      return; // Não prosseguir
    }

    // Se valor for OK, permitir selecionar boleto
    console.log('✅ Valor OK para boleto, permitindo seleção');
    setPayment(prev => ({ ...prev, method: 'boleto' }));
  };

  const processPurchase = async (tokenFromPagarme = null) => {
    try {
      console.log('🚀 INICIANDO processPurchase...');
      setProcessing(true);

      // DEBUG: Verificar todos os dados antes de processar
      console.log('🔍 DEBUG - Dados do cliente:', customer);
      console.log('🔍 DEBUG - Dados de entrega:', shipping);
      console.log('🔍 DEBUG - Dados de pagamento:', payment);
      console.log('🔍 DEBUG - Token do estado:', paymentToken);
      console.log('🔍 DEBUG - Token do parâmetro:', tokenFromPagarme);
      
      // Usar o token passado como parâmetro (mais recente) ou do estado
      const finalToken = tokenFromPagarme || paymentToken;
      console.log('🎯 DEBUG - Token final a ser usado:', finalToken);

      // Validações detalhadas
      const errors = [];

      // Dados pessoais
      if (!customer.name?.trim()) errors.push('Nome completo');
      if (!customer.email?.trim()) errors.push('E-mail');
      if (!customer.phone?.trim()) errors.push('Celular');
      if (!customer.document?.trim()) errors.push('CPF');

      // Validação específica de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (customer.email && !emailRegex.test(customer.email)) {
        errors.push('E-mail válido');
      }

      // Validação específica de CPF (11 dígitos)
      if (customer.document && customer.document.replace(/\D/g, '').length !== 11) {
        errors.push('CPF com 11 dígitos');
      }

      // Validação específica de celular (10 ou 11 dígitos)
      if (customer.phone && ![10, 11].includes(customer.phone.replace(/\D/g, '').length)) {
        errors.push('Celular válido');
      }

      // ⚠️ Endereço obrigatório APENAS para produtos FÍSICOS
      const isPhysicalProduct = product?.shipping !== null && product?.shipping !== undefined;
      
      if (isPhysicalProduct) {
        // Validações de endereço para produtos físicos
        if (!shipping.zipCode?.trim()) errors.push('CEP');
        if (!shipping.street?.trim()) errors.push('Rua/Logradouro');
        if (!shipping.number?.trim()) errors.push('Número');
        if (!shipping.neighborhood?.trim()) errors.push('Bairro');
        if (!shipping.city?.trim()) errors.push('Cidade');
        if (!shipping.state?.trim()) errors.push('Estado');

        // Validação específica de CEP (8 dígitos)
        if (shipping.zipCode && shipping.zipCode.replace(/\D/g, '').length !== 8) {
          errors.push('CEP válido');
        }
      } else {
        console.log('📱 Produto DIGITAL - Validações de endereço IGNORADAS');
      }

      // Validação específica para cartão de crédito - Pagar.me
      if (payment.method === 'credit_card') {
        if (!pagarmeLoaded) {
          errors.push('Sistema de pagamento ainda não foi carregado');
        }
        
        if (pagarmeError) {
          errors.push('Erro no sistema de pagamento');
        }
        
        // VALIDAÇÃO OBRIGATÓRIA DO TOKEN
        if (!finalToken) {
          errors.push('Token de pagamento é obrigatório para cartão de crédito');
        }
        
        console.log('🔍 DEBUG - Estado atual da tokenização:', {
          pagarmeLoaded,
          pagarmeError,
          paymentToken: paymentToken ? 'PRESENTE' : 'AUSENTE',
          tokenFromPagarme: tokenFromPagarme ? 'PRESENTE' : 'AUSENTE',
          finalToken: finalToken ? 'PRESENTE' : 'AUSENTE'
        });
      }

      if (errors.length > 0) {
        throw new Error(`Preencha ou corrija os seguintes campos: ${errors.join(', ')}`);
      }

      // ✅ VALIDAÇÃO DE BOLETO JÁ FOI FEITA NO handleBoletoClick()
      // Não precisa validar novamente aqui (evita modal duplicado)

      // Determinar dados de afiliado (priorizar cookie, fallback para URL)
      const finalAffiliateData = affiliateData || {
        affiliate_id: affiliateId,
        ref: affiliateRef,
        utm_source: utmSource
      };

      // O preço JÁ VEM EM CENTAVOS do backend - apenas garantir que é número
      const rawPrice = product?.plans?.[0]?.price || 0;
      const priceInCents = parseInt(parseFloat(rawPrice)); // Não multiplicar por 100!

      console.log('💰 Debug valores:', {
        rawPrice,
        rawPriceType: typeof rawPrice,
        priceInCents,
        'rawPrice já é centavos': true,
        'equivale a reais': (priceInCents / 100).toFixed(2),
        product: product?.plans?.[0]
      });

      const saleData = {
        product_id: product?.id, // Usar ID numérico do produto carregado (nunca parseInt do productCode)
        plan_id: product?.plans?.[0]?.id || null,
        customer_data: customer,
        shipping_address: shipping, // Campo correto que o backend espera
        affiliate_data: finalAffiliateData,
        payment_method: payment.method,
        payment_data: payment.method === 'credit_card' ? {
          payment_token: finalToken,
          installments: payment.installments || 1
        } : null, // Token da Pagar.me dentro de payment_data
        installments: payment.installments || 1,
        total_amount: priceInCents, // Enviando em centavos
        // Manter campos legacy para compatibilidade
        affiliate_id: finalAffiliateData.affiliate_id || affiliateId || null,
        utm_source: finalAffiliateData.utm_source || utmSource
      };

          console.log('🚀 Enviando dados para checkout:', {
            ...saleData,
            payment_token: saleData.payment_token ? '***TOKEN_PRESENTE***' : null
          });

          console.log('🔍 DEBUG - saleData completo:', JSON.stringify(saleData, null, 2));

          // MODAL DE DEBUG ANTES DE ENVIAR
          const tokenStatus = saleData.payment_data?.payment_token ? 'PRESENTE ✅' : 'AUSENTE ❌';
          
          console.log('🔍 DEBUG - Verificando tokenStatus:', tokenStatus);
          const debugInfo = `🎯 DADOS ANTES DO ENVIO:

💳 Token Pagar.me: ${tokenStatus}
🛒 Produto ID: ${saleData.product_id}
👤 Cliente: ${saleData.customer_data.name}
📧 Email: ${saleData.customer_data.email}
💰 Valor: R$ ${(saleData.total_amount / 100).toFixed(2)}
🔢 Parcelas: ${saleData.installments}

🚀 Enviando para: ${process.env.NODE_ENV === 'production' ? 'PRODUÇÃO' : 'DESENVOLVIMENTO'}`;

          console.log('🔍 DEBUG - Pulando modal para evitar travamento...');
          console.log('📋 DADOS ANTES DO ENVIO:', debugInfo);
          
          // REMOVIDO: alert(debugInfo) - estava causando travamento
          // try {
          //   alert(debugInfo);
          //   console.log('✅ Modal mostrado com sucesso');
          // } catch (modalError) {
          //   console.error('❌ Erro ao mostrar modal:', modalError);
          // }

          console.log('🌐 URL do backend:', API_URL);
          console.log('🔍 DEBUG - Iniciando requisição...');
          
          let response;
          try {
            response = await fetch(`${API_URL}/api/checkout/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
            });
          } catch (networkError) {
            console.error('❌ ERRO DE REDE:', networkError);
            
            const networkErrorInfo = `❌ ERRO DE CONEXÃO:

Não foi possível conectar ao servidor!

URL: ${API_URL}/api/checkout/process
Erro: ${networkError.message}
Token: ${saleData.payment_token ? 'PRESENTE ✅' : 'AUSENTE ❌'}

Verifique sua conexão com a internet.`;
            
            console.error('❌ ERRO DE REDE:', networkErrorInfo);
            throw networkError;
          }

          console.log('📡 Resposta do servidor:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ ERRO DO BACKEND:', errorData);
        
        // MODAL DE ERRO DETALHADO
        const errorInfo = `❌ ERRO NO BACKEND:

Status: ${response.status} ${response.statusText}
Mensagem: ${errorData.message || 'Erro desconhecido'}
Detalhes: ${JSON.stringify(errorData, null, 2)}

Token estava presente: ${saleData.payment_data?.payment_token ? 'SIM ✅' : 'NÃO ❌'}
URL: ${API_URL}/api/checkout/process`;
        
        console.error('❌ ERRO DO BACKEND:', errorInfo);
        
        // ✅ MOSTRAR MODAL DE ERRO E PARAR EXECUÇÃO (não throw, para não cair no catch)
        setErrorDetails({
          message: 'Não foi possível processar o pagamento',
          error: errorData.error || errorData.message || 'Erro desconhecido ao processar sua compra'
        });
        setShowErrorModal(true);
        setProcessing(false);
        return; // Parar aqui, não continuar nem lançar exceção
      }

      const responseData = await response.json();
      
      console.log('🎉 VENDA PROCESSADA COM SUCESSO!', responseData);
      console.log('📊 DADOS DA VENDA CRIADA:', {
        id: responseData.data?.id,
        total: responseData.data?.total_amount,
        status: responseData.data?.status,
        payment_method: responseData.data?.payment_method,
        created_at: responseData.data?.created_at
      });
      
      // LOG DE SUCESSO
      console.log('🎉 VENDA PROCESSADA COM SUCESSO!');
      console.log('Token usado:', saleData.payment_data?.payment_token);
      console.log('Resposta completa:', responseData);
      
      // Preparar orderData ANTES dos ifs (para usar no modal e na página de sucesso)
      const orderData = responseData.data; // Usar dados completos do backend
      
      // Verificar se é pagamento PIX e tem dados do QR Code
      if (payment.method === 'pix' && responseData.data?.payment_info) {
        console.log('💳 Pagamento PIX detectado - mostrando modal');
        console.log('📦 Order ID da API:', responseData.data.payment_order_id);
        setPixData({
          qr_code: responseData.data.payment_info.qr_code,
          qr_code_url: responseData.data.payment_info.qr_code_url,
          expires_at: responseData.data.payment_info.expires_at,
          sale_id: responseData.data.id,
          order_id: responseData.data.payment_order_id, // ID do pedido na API de Payments (ex: or_xxxxx)
          total: responseData.data.total_amount || saleData.total_amount // Manter em centavos para formatCurrency
        });
        setShowPixModal(true);
        setProcessing(false);
        
        // Limpar dados do formulário
        localStorage.removeItem('checkout-form-data');
        return; // Não redirecionar ainda, esperar o usuário fechar o modal
      }
      
      // Verificar se é pagamento Boleto e tem dados do boleto
      if (payment.method === 'boleto' && responseData.data?.payment_info?.barcode) {
        console.log('🎫 Pagamento Boleto detectado - mostrando modal');
        console.log('📋 Dados do boleto:', responseData.data.payment_info);
        
        // Salvar orderData completo no sessionStorage para usar no modal
        sessionStorage.setItem('last-order-data', JSON.stringify(orderData));
        
        // Passar o OBJETO COMPLETO do payment_info (igual ao backup)
        const boletoInfo = responseData.data.payment_info;
        setPaymentInfo(boletoInfo);
        setShowBoletoModal(true);
        setProcessing(false);
        
        // Limpar dados do formulário
        localStorage.removeItem('checkout-form-data');
        return; // Não redirecionar ainda, esperar o usuário fechar o modal
      }
      
      // Para outros métodos de pagamento, redirecionar normalmente
      console.log('🔄 Redirecionando para página de sucesso...');
      
      // LIMPAR DADOS SALVOS E REDIRECIONAR
      localStorage.removeItem('checkout-form-data');
      
      // Usar navigate com state em vez de window.location.href
      navigate('/checkout/success', { state: { order: orderData } });

    } catch (err) {
      console.error('❌ ERRO CAPTURADO no checkout:', err);
      console.error('❌ Stack trace completo:', err.stack);
      console.error('❌ Tipo do erro:', typeof err);
      console.error('❌ Nome do erro:', err.name);
      
      // LOGS DETALHADOS DO ERRO
      const criticalError = `❌ ERRO CRÍTICO CAPTURADO:

Tipo: ${err.name || 'Unknown'}
Mensagem: ${err.message || 'Sem mensagem'}
Stack: ${err.stack || 'Sem stack trace'}

Token estava presente: ${tokenFromPagarme ? 'SIM ✅' : 'NÃO ❌'}

Este erro está causando o refresh!`;
      
      console.error('📋 ERRO COMPLETO:', criticalError);
      
      // ✅ MOSTRAR MODAL DE ERRO PROFISSIONAL
      setErrorDetails({
        message: 'Não foi possível processar o pagamento',
        error: err.message || 'Erro desconhecido ao processar sua compra'
      });
      setShowErrorModal(true);
    } finally {
      console.log('🔚 FINALIZANDO processPurchase...');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="landing-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="landing-error">
        <div className="container">
          <div className="error-content">
            <Icon icon="mdi:alert-circle" className="error-icon" />
            <h2>Produto não encontrado</h2>
            <p>{error || 'Este produto não está disponível no momento.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-checkout">
      {/* Banner Personalizado Novo (1200x400px) */}
      {checkout?.banner_url && (
        <div className="checkout-banner-custom" style={{ 
          width: '100%', 
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          marginBottom: '20px',
          paddingTop: '20px'
        }}>
          <img 
            src={checkout.banner_url} 
            alt="Banner do Checkout"
            style={{ 
              maxWidth: '1200px',
              width: '100%',
              height: 'auto',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              display: 'block'
            }}
          />
        </div>
      )}

      {/* Banner Template Personalizado */}
      {checkout?.custom_colors?.banner_config?.enabled && (
        <div className={`custom-banner template-${checkout.custom_colors.banner_config.template || 'gradient_promo'}`}>
          <div className="banner-content">
            <div className="container">
              <div className="banner-text">
                <h2 style={{ color: checkout.custom_colors.banner_config.text_color || 'white' }}>
                  {checkout.custom_colors.banner_config.title || '🎉 OFERTA ESPECIAL!'}
                </h2>
                <p style={{ color: checkout.custom_colors.banner_config.text_color || 'white' }}>
                  {checkout.custom_colors.banner_config.subtitle || 'Aproveite nossa promoção!'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner de Imagem (legacy) */}
      {checkout?.custom_colors?.banner_image && !checkout?.custom_colors?.banner_config?.enabled && (
        <div className="custom-banner">
          <img 
            src={checkout.custom_colors.banner_image} 
            alt="Banner promocional"
            className="banner-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Banner de Promoção (Condicional) */}
      {showTimer && timeLeft && (
        <div className="promo-banner">
          <div className="container">
            <div className="promo-content">
              <div className="promo-text">
                <Icon icon="mdi:clock-fast" />
                <span>{checkout?.counter_config?.message || 'Promoção por Tempo Limitado'}</span>
              </div>
              <div className="countdown">
                <div className="time-unit">
                  <span className="number">{String(timeLeft.hours).padStart(2, '0')}</span>
                </div>
                <span className="separator">:</span>
                <div className="time-unit">
                  <span className="number">{String(timeLeft.minutes).padStart(2, '0')}</span>
                </div>
                <span className="separator">:</span>
                <div className="time-unit">
                  <span className="number">{String(timeLeft.seconds).padStart(2, '0')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo Principal */}
      <div className="main-content">
        <div className="container">
          <div className="row">
            {/* Coluna do Produto */}
            <div className="col-lg-6">
              <div className="product-showcase">
                {/* Imagem do Produto */}
                <div className="product-image">
                  <img 
                    src={product.image || "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg"}
                    alt={product.name} 
                    style={{ width: '100%', height: 'auto', borderRadius: '12px' }}
                    onError={(e) => {
                      e.target.src = "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg";
                    }}
                  />

                </div>

                {/* Informações do Produto */}
                <div className="product-info">
                  <span className="product-title">{product.name}</span>
                  
                  <div className="product-pricing">
                    {product.plans && product.plans.length > 0 && (
                        <div className="price-section main-price">
                          <span className="current-price">{formatCurrency(parseFloat(product.plans[0].price))}</span>
                        </div>
                    )}
                  </div>

                  {/* V2 — composição do kit (mostra quando plano tem 2+ itens OU 1 item com qty > 1) */}
                  {planItems && planItems.length > 0 && (planItems.length > 1 || (planItems[0]?.quantity || 1) > 1) && (
                    <div style={{
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginTop: '12px',
                    }}>
                      <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                        🎁 Este kit inclui:
                      </span>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {planItems.map((it, idx) => {
                          const name = it.physical_product?.name || it.name || `Item ${idx + 1}`;
                          const qty = it.quantity || 1;
                          return (
                            <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#4b5563' }}>
                              <span>• {name}</span>
                              <span style={{ fontWeight: 600 }}>{qty}×</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Descrição do Produto */}
                  <div className="product-description">
                    <span className="section-title">Sobre este produto:</span>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: product.description || 'Produto de alta qualidade com excelente custo-benefício.' 
                      }}
                    />
                  </div>

                  <div className="product-benefits">
                    <span className="section-title">Você está adquirindo:</span>
                    <ul>
                      <li>
                        <Icon icon="mdi:check-circle" />
                        <span>{product.name}</span>
                      </li>
                      {product.plans && product.plans[0] && product.plans[0].included_items_quantity && (
                        <li>
                          <Icon icon="mdi:check-circle" />
                          <span>{product.plans[0].included_items_quantity} itens inclusos</span>
                        </li>
                      )}
                      {product.plans && product.plans[0] && product.plans[0].max_installments > 1 && (
                        <li>
                          <Icon icon="mdi:check-circle" />
                          <span>Parcelamento em até {product.plans[0].max_installments}x</span>
                        </li>
                      )}
                      <li>
                        <Icon icon="mdi:check-circle" />
                        <span>Suporte especializado</span>
                      </li>
                      <li>
                        <Icon icon="mdi:check-circle" />
                        <span>Garantia de 7 dias</span>
                      </li>
                    </ul>
                  </div>


                </div>
              </div>
            </div>

            {/* Coluna do Formulário */}
            <div className="col-lg-6">
              <div className="checkout-form">
                <div className="form-header">
                  <span className="section-title">Complete seus dados</span>
                  <p>Preencha as informações abaixo para finalizar sua compra</p>
                </div>

                <form data-pagarmecheckout-form onSubmit={handleFormSubmit}>

                  {/* Dados Pessoais */}
                  <div className="form-section">
                    <span className="section-title">
                      <Icon icon="mdi:account" />
                      Seus Dados Pessoais
                    </span>
                    
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Nome Completo *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={customer.name}
                            onChange={(e) => setCustomer(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Digite seu nome completo"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>E-mail *</label>
                          <input
                            type="email"
                            className="form-control"
                            value={customer.email}
                            onChange={(e) => setCustomer(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="seuemail@exemplo.com"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Celular *</label>
                          <input
                            type="tel"
                            className="form-control"
                            value={customer.phone}
                            onChange={(e) => {
                              const formatted = formatPhone(e.target.value);
                              setCustomer(prev => ({ ...prev, phone: formatted }));
                            }}
                            placeholder="(11) 99999-9999"
                            maxLength="15"
                            required
                          />
                          <div className="mt-2">
                            <ModernSwitch
                              label="Este número tem WhatsApp"
                              checked={customer.isWhatsApp}
                              onChange={(checked) => setCustomer(prev => ({ ...prev, isWhatsApp: checked }))}
                              size="sm"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>CPF *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={customer.document}
                            onChange={(e) => {
                              const formatted = formatCPF(e.target.value);
                              setCustomer(prev => ({ ...prev, document: formatted }));
                            }}
                            placeholder="000.000.000-00"
                            maxLength="14"
                            required
                          />
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Endereço de Entrega */}
                  <div className="form-section">
                    <span className="section-title">
                      <Icon icon="mdi:map-marker" />
                      Endereço de Entrega
                    </span>
                    
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>CEP *</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className={`form-control ${cepError ? 'is-invalid' : ''}`}
                              value={shipping.zipCode}
                              onChange={(e) => {
                                const formatted = cepService.format(e.target.value);
                                setShipping(prev => ({ ...prev, zipCode: formatted }));
                                clearCepError();
                                
                                // Buscar endereço quando CEP tiver 8 dígitos
                                if (formatted.replace(/\D/g, '').length === 8) {
                                  handleCEPLookup(formatted);
                                }
                              }}
                              placeholder="00000-000"
                              maxLength="9"
                              required
                            />
                            {cepLoading && (
                              <div className="input-group-append">
                                <span className="input-group-text">
                                  <Icon icon="mdi:loading" className="spin" />
                                </span>
                              </div>
                            )}
                          </div>
                          {cepError && (
                            <div className="invalid-feedback d-block">
                              <Icon icon="mdi:alert-circle" className="me-1" />
                              {cepError}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-md-8">
                        <div className="form-group">
                          <label>Rua/Logradouro *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={shipping.street}
                            onChange={(e) => setShipping(prev => ({ ...prev, street: e.target.value }))}
                            placeholder="Nome da rua, avenida, etc."
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Número *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={shipping.number}
                            onChange={(e) => setShipping(prev => ({ ...prev, number: e.target.value }))}
                            placeholder="123"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Complemento</label>
                          <input
                            type="text"
                            className="form-control"
                            value={shipping.complement}
                            onChange={(e) => setShipping(prev => ({ ...prev, complement: e.target.value }))}
                            placeholder="Apt, Bloco, etc."
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Bairro *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={shipping.neighborhood}
                            onChange={(e) => setShipping(prev => ({ ...prev, neighborhood: e.target.value }))}
                            placeholder="Nome do bairro"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Cidade *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={shipping.city}
                            onChange={(e) => setShipping(prev => ({ ...prev, city: e.target.value }))}
                            placeholder="Nome da cidade"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Estado *</label>
                          <select
                            className="form-control"
                            value={shipping.state}
                            onChange={(e) => setShipping(prev => ({ ...prev, state: e.target.value }))}
                            required
                          >
                            <option value="">Selecione o estado</option>
                            <option value="AC">Acre</option>
                            <option value="AL">Alagoas</option>
                            <option value="AP">Amapá</option>
                            <option value="AM">Amazonas</option>
                            <option value="BA">Bahia</option>
                            <option value="CE">Ceará</option>
                            <option value="DF">Distrito Federal</option>
                            <option value="ES">Espírito Santo</option>
                            <option value="GO">Goiás</option>
                            <option value="MA">Maranhão</option>
                            <option value="MT">Mato Grosso</option>
                            <option value="MS">Mato Grosso do Sul</option>
                            <option value="MG">Minas Gerais</option>
                            <option value="PA">Pará</option>
                            <option value="PB">Paraíba</option>
                            <option value="PR">Paraná</option>
                            <option value="PE">Pernambuco</option>
                            <option value="PI">Piauí</option>
                            <option value="RJ">Rio de Janeiro</option>
                            <option value="RN">Rio Grande do Norte</option>
                            <option value="RS">Rio Grande do Sul</option>
                            <option value="RO">Rondônia</option>
                            <option value="RR">Roraima</option>
                            <option value="SC">Santa Catarina</option>
                            <option value="SP">São Paulo</option>
                            <option value="SE">Sergipe</option>
                            <option value="TO">Tocantins</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Cálculo de Frete */}
                    {shipping.zipCode && shipping.zipCode.replace(/\D/g, '').length === 8 && (
                      <div className="shipping-calculation">
                        <div className="shipping-info">
                          {shippingCost.loading ? (
                            <div className="shipping-loading">
                              <Icon icon="mdi:loading" className="spin me-2" />
                              Calculando frete...
                            </div>
                          ) : shippingCost.freeShipping ? (
                            <div className="shipping-free">
                              <Icon icon="mdi:truck" className="me-2 text-success" />
                              <span className="text-success fw-bold">Frete Grátis!</span>
                            </div>
                          ) : (
                            <div className="shipping-cost">
                              <Icon icon="mdi:truck" className="me-2" />
                              <span>Frete: <strong>{formatCurrency(shippingCost.cost)}</strong></span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Método de Pagamento */}
                  <div className="form-section">
                    <span className="section-title">
                      <Icon icon="mdi:credit-card" />
                      Forma de Pagamento
                    </span>

                    <div className="row g-2">
                      {checkoutMethods.pix && (
                        <div className="col-md-6">
                          <div 
                            className={`payment-option ${payment.method === 'pix' ? 'active' : ''}`}
                            onClick={() => setPayment(prev => ({ ...prev, method: 'pix' }))}
                          >
                            <Icon icon="mdi:qrcode" />
                            <span>PIX</span>
                            <Icon icon="mdi:check-circle" className="check-icon" />
                          </div>
                        </div>
                      )}

                      {checkoutMethods.boleto && (
                        <div className="col-md-6">
                          <div 
                            className={`payment-option ${payment.method === 'boleto' ? 'active' : ''}`}
                            onClick={handleBoletoClick}
                          >
                            <Icon icon="mdi:file-document" />
                            <span>Boleto Bancário</span>
                            <Icon icon="mdi:check-circle" className="check-icon" />
                          </div>
                        </div>
                      )}

                      {checkoutMethods.after_pay && (
                        <div className="col-md-6">
                          <div 
                            className={`payment-option ${payment.method === 'after_pay' ? 'active' : ''}`}
                            onClick={() => setPayment(prev => ({ ...prev, method: 'after_pay' }))}
                          >
                            <Icon icon="mdi:credit-card-clock" />
                            <span>AfterPay</span>
                            <Icon icon="mdi:check-circle" className="check-icon" />
                          </div>
                        </div>
                      )}

                      {checkoutMethods.credit_card && (
                        <div className="col-md-6">
                          <div 
                            className={`payment-option ${payment.method === 'credit_card' ? 'active' : ''}`}
                            onClick={() => setPayment(prev => ({ ...prev, method: 'credit_card' }))}
                          >
                            <Icon icon="mdi:credit-card" />
                            <span>Cartão de Crédito</span>
                            <Icon icon="mdi:check-circle" className="check-icon" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Campos de Cartão de Crédito - Mapeados para Pagar.me */}
                    {payment.method === 'credit_card' && (
                      <div className="credit-card-fields">

                        <div className="form-group">
                          <label>
                            <Icon icon="mdi:credit-card" />
                            NÚMERO DO CARTÃO
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            data-pagarmecheckout-element="number"
                            placeholder="Digite somente números do cartão"
                            maxLength="19"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            <Icon icon="mdi:account" />
                            NOME DO TITULAR
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            data-pagarmecheckout-element="holder_name"
                            placeholder="Digite o nome completo impresso no cartão"
                            required
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>VALIDADE</label>
                            <div className="expiry-inputs">
                              <input
                                type="text"
                                className="form-control"
                                data-pagarmecheckout-element="exp_month"
                                placeholder="MM"
                                maxLength="2"
                                required
                              />
                              <span className="separator">/</span>
                              <input
                                type="text"
                                className="form-control"
                                data-pagarmecheckout-element="exp_year"
                                placeholder="AAAA"
                                maxLength="4"
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label>
                              <Icon icon="mdi:lock" />
                              CVC
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              data-pagarmecheckout-element="cvv"
                              placeholder="Digite o CVC do cartão"
                              maxLength="4"
                              required
                            />
                          </div>
                        </div>


                        <div className="form-group">
                          <label>PARCELAMENTO DO CARTÃO</label>
                          <select 
                            className="form-control"
                            value={payment.installments}
                            onChange={(e) => setPayment(prev => ({ ...prev, installments: parseInt(e.target.value) }))}
                          >
                            <option value={1}>1x de {formatCurrency(parseFloat(product.plans?.[0]?.price || 0))}</option>
                            <option value={2}>2x de {formatCurrency(parseFloat(product.plans?.[0]?.price || 0) / 2)}</option>
                            <option value={3}>3x de {formatCurrency(parseFloat(product.plans?.[0]?.price || 0) / 3)}</option>
                            <option value={6}>6x de {formatCurrency(parseFloat(product.plans?.[0]?.price || 0) / 6)}</option>
                            <option value={12}>12x de {formatCurrency(parseFloat(product.plans?.[0]?.price || 0) / 12)}</option>
                          </select>
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Resumo do Pedido - Sempre Visível */}
                  <div className="order-summary">
                    <div className="summary-line">
                      <span>Produto:</span>
                      <span>{product.name}</span>
                    </div>
                    <div className="summary-line">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(parseFloat(product.plans?.[0]?.price || 0))}</span>
                    </div>
                    {shippingCost.freeShipping ? (
                      <div className="summary-line">
                        <span>Frete:</span>
                        <span className="text-success">Grátis</span>
                      </div>
                    ) : shippingCost.cost > 0 ? (
                      <div className="summary-line">
                        <span>Frete:</span>
                        <span>{formatCurrency(shippingCost.cost)}</span>
                      </div>
                    ) : null}
                    <div className="summary-line total">
                      <span>Total:</span>
                      <span>{formatCurrency(parseFloat(product.plans?.[0]?.price || 0) + (shippingCost.freeShipping ? 0 : shippingCost.cost))}</span>
                    </div>
                  </div>

                  {/* Botão de Compra */}
                  <div className="purchase-section">
                    <button
                      type="submit"
                      className="btn-purchase"
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Processando...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:lock" />
                          COMPRAR AGORA - R$ {formatCurrency(parseFloat(product.plans?.[0]?.price || 0) + (shippingCost.freeShipping ? 0 : shippingCost.cost))}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Selos de Segurança */}
                  <div className="security-seals">
                    <div className="seal">
                      <Icon icon="mdi:shield-check" />
                      <span>Compra Segura</span>
                    </div>
                    <div className="seal">
                      <Icon icon="mdi:lock" />
                      <span>Dados Protegidos</span>
                    </div>
                    <div className="seal">
                      <Icon icon="mdi:medal" />
                      <span>Garantia 7 dias</span>
                    </div>
                  </div>

                  {/* Telefones de Contato */}
                  {(checkout?.support_phone || checkout?.whatsapp_phone) && (
                    <div className="contact-support">
                      <span className="section-title">Precisa de ajuda?</span>
                      <div className="support-buttons">
                        {checkout.support_phone && (
                          <a 
                            href={`tel:${checkout.support_phone}`}
                            className="support-btn"
                          >
                            <Icon icon="mdi:phone" />
                            <span>Suporte: {checkout.support_phone}</span>
                          </a>
                        )}
                        {checkout.whatsapp_phone && (
                          <a 
                            href={`https://wa.me/${checkout.whatsapp_phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="support-btn whatsapp"
                          >
                            <Icon icon="mdi:whatsapp" />
                            <span>WhatsApp: {checkout.whatsapp_phone}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer de Segurança */}
      <div className="security-footer">
        <div className="container">
          <div className="security-badge-footer">
            <Icon icon="mdi:shield-check" />
            <span>COMPRA 100% SEGURA</span>
          </div>
          <p style={{ marginTop: '15px' }}>
            Esta compra será processada pela Pagmus © 2025 - Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* Modal PIX - Design Moderno */}
      <Modal
        show={showPixModal}
        onHide={() => {}}
        backdrop="static"
        keyboard={false}
        size="md"
        centered
        className="pix-modal"
      >
        <Modal.Body className="p-0">
          {pixData && (
            <div className="text-center">
              {/* Header com gradiente */}
              <div style={{
                background: 'linear-gradient(135deg, #32BCAD 0%, #1a8a7d 100%)',
                padding: '24px 20px',
                borderRadius: '8px 8px 0 0'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <Icon icon="ic:baseline-pix" style={{ fontSize: '32px', color: 'white' }} />
                </div>
                <h4 style={{ color: 'white', margin: 0, fontWeight: 600 }}>
                  Pagamento via PIX
                </h4>
                <p style={{ color: 'rgba(255,255,255,0.9)', margin: '8px 0 0', fontSize: '14px' }}>
                  Escaneie o QR Code ou copie o código
                </p>
              </div>

              {/* Conteúdo */}
              <div style={{ padding: '24px' }}>
                {/* QR Code */}
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  {pixData.qr_code ? (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pixData.qr_code)}`}
                      alt="QR Code PIX"
                      style={{ width: '200px', height: '200px' }}
                    />
                  ) : (
                    <div className="text-muted">QR Code não disponível</div>
                  )}
                </div>

                {/* Valor */}
                <div style={{
                  background: '#e8f5e9',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}>
                  <span style={{ color: '#2e7d32', fontSize: '14px' }}>Valor a pagar:</span>
                  <div style={{ color: '#1b5e20', fontSize: '24px', fontWeight: 700 }}>
                    {formatCurrency(pixData.total)}
                  </div>
                </div>

                {/* Código Copia e Cola */}
                {pixData.qr_code && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ fontSize: '13px', color: '#666', marginBottom: '8px', display: 'block' }}>
                      Código PIX (Copia e Cola):
                    </label>
                    <div style={{
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <input
                        type="text"
                        value={pixData.qr_code}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '12px',
                          background: '#f8f9fa',
                          color: '#666'
                        }}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(pixData.qr_code);
                          // Feedback visual temporário
                          const btn = document.getElementById('copy-pix-btn');
                          if (btn) {
                            btn.innerHTML = '<span style="color: #4caf50">Copiado!</span>';
                            setTimeout(() => {
                              btn.innerHTML = 'Copiar';
                            }, 2000);
                          }
                        }}
                        id="copy-pix-btn"
                        style={{
                          padding: '10px 20px',
                          background: '#32BCAD',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Copiar
                      </button>
                    </div>
                  </div>
                )}

                {/* Status de verificação */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  background: '#fff3e0',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div className="spinner-border spinner-border-sm text-warning" role="status" />
                  <span style={{ color: '#e65100', fontSize: '14px' }}>
                    Aguardando confirmação do pagamento...
                  </span>
                </div>

                {/* Expiração */}
                {pixData.expires_at && (
                  <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>
                    Válido até: {new Date(pixData.expires_at).toLocaleString('pt-BR')}
                  </p>
                )}
              </div>

              {/* Footer */}
              <div style={{
                borderTop: '1px solid #eee',
                padding: '16px 24px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => {
                    if (window.confirm('O pagamento ainda está pendente. Deseja realmente sair? Você receberá um email quando o pagamento for confirmado.')) {
                      setShowPixModal(false);
                      navigate('/');
                    }
                  }}
                  style={{
                    padding: '10px 24px',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancelar e voltar
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal de Erro Detalhado */}
      <Modal 
        show={showErrorModal} 
        onHide={() => {
          setShowErrorModal(false);
          setErrorDetails(null);
          // Limpar token processado para permitir nova tentativa
          sessionStorage.removeItem('processed-token');
          // Redirecionar de volta para o checkout
          window.location.href = window.location.href.split('?')[0];
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{ backgroundColor: '#dc3545', color: 'white' }}>
          <Modal.Title>
            <Icon icon="mdi:alert-circle" className="me-2" />
            Erro ao Processar Pagamento
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorDetails && (
            <div>
              <div className="alert alert-danger mb-3">
                <h5 className="mb-2">
                  <Icon icon="mdi:information" className="me-2" />
                  Status: {errorDetails.status}
                </h5>
                <p className="mb-1"><strong>Mensagem:</strong> {errorDetails.message}</p>
                <p className="mb-0"><strong>Erro:</strong> {errorDetails.error}</p>
              </div>

              {errorDetails.details && (
                <div className="alert alert-warning">
                  <h6 className="mb-2">
                    <Icon icon="mdi:file-document" className="me-2" />
                    Detalhes Adicionais:
                  </h6>
                  <pre style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '10px', 
                    borderRadius: '5px',
                    fontSize: '12px',
                    maxHeight: '200px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(errorDetails.details, null, 2)}
                  </pre>
                </div>
              )}

              <div className="alert alert-info">
                <h6 className="mb-2">
                  <Icon icon="mdi:code-json" className="me-2" />
                  Resposta Completa da API:
                </h6>
                <pre style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '10px', 
                  borderRadius: '5px',
                  fontSize: '11px',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(errorDetails.fullError, null, 2)}
                </pre>
              </div>

              <div className="alert alert-secondary">
                <Icon icon="mdi:lightbulb" className="me-2" />
                <strong>O que fazer:</strong>
                <ul className="mb-0 mt-2">
                  <li>Verifique se todos os dados do cartão estão corretos</li>
                  <li>Certifique-se de que o cartão tem limite disponível</li>
                  <li>Tente novamente ou use outro método de pagamento</li>
                  <li>Se o erro persistir, entre em contato com o suporte</li>
                </ul>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowErrorModal(false);
              setErrorDetails(null);
              sessionStorage.removeItem('processed-token');
              window.location.href = window.location.href.split('?')[0];
            }}
          >
            <Icon icon="mdi:arrow-left" className="me-2" />
            Voltar ao Checkout
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              // Copiar erro para área de transferência
              navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
              alert('Detalhes do erro copiados para a área de transferência!');
            }}
          >
            <Icon icon="mdi:content-copy" className="me-2" />
            Copiar Erro
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Boleto */}
      {showBoletoModal && paymentInfo && (
        <div className="payment-modal-overlay" onClick={() => {
          setShowBoletoModal(false);
          // Passar orderData completo do backend (já tem customer, product, etc.)
          const orderDataFromStorage = JSON.parse(sessionStorage.getItem('last-order-data') || '{}');
          navigate('/checkout/success', { state: { order: orderDataFromStorage } });
        }}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h2><Icon icon="mdi:barcode" /> Pagamento via Boleto</h2>
              <button onClick={() => {
                setShowBoletoModal(false);
                const orderDataFromStorage = JSON.parse(sessionStorage.getItem('last-order-data') || '{}');
                navigate('/checkout/success', { state: { order: orderDataFromStorage } });
              }}>
                <Icon icon="mdi:close" />
              </button>
            </div>
            <div className="payment-modal-body">
              <p className="payment-instruction">Pague o boleto até a data de vencimento:</p>
              
              {paymentInfo.due_date && (
                <div className="boleto-info-box">
                  <Icon icon="mdi:calendar" />
                  <strong>Vencimento:</strong> {new Date(paymentInfo.due_date).toLocaleDateString('pt-BR')}
                </div>
              )}
              
              <div className="boleto-barcode">
                <label>Código de Barras:</label>
                <div className="copy-paste-box">
                  <input 
                    type="text" 
                    value={paymentInfo.barcode || 'Código de barras'} 
                    readOnly 
                  />
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(paymentInfo.barcode);
                      alert('Código de barras copiado!');
                    }}
                    className="copy-button"
                  >
                    <Icon icon="mdi:content-copy" /> Copiar
                  </button>
                </div>
              </div>
              
              {paymentInfo.url && (
                <div className="boleto-download">
                  <a 
                    href={paymentInfo.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-download-boleto"
                  >
                    <Icon icon="mdi:file-pdf-box" /> Baixar Boleto PDF
                  </a>
                </div>
              )}
              
              {paymentInfo.instructions && (
                <div className="boleto-instructions">
                  <Icon icon="mdi:information-outline" />
                  {paymentInfo.instructions}
                </div>
              )}
            </div>
            <div className="payment-modal-footer">
              <button 
                onClick={() => {
                  setShowBoletoModal(false);
                  navigate('/checkout/success', { state: { order: { ...paymentInfo, payment_method: 'boleto' } } });
                }}
                className="btn-continue"
              >
                Continuar para Página de Sucesso
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Erro Profissional */}
      {showErrorModal && errorDetails && (
        <div className="payment-modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="payment-modal error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header error">
              <Icon icon="mdi:alert-circle" className="error-icon-large" />
              <h3>Erro ao Processar Pagamento</h3>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowErrorModal(false)}
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
            <div className="payment-modal-body">
              <div className="error-message-box">
                <p className="error-message-main">{errorDetails.message}</p>
                
                {errorDetails.error && (
                  <div className="error-details">
                    <p><strong>Detalhes:</strong></p>
                    <p className="error-technical">{errorDetails.error}</p>
                  </div>
                )}
                
                <div className="error-suggestions">
                  <p><strong>O que você pode fazer:</strong></p>
                  <ul>
                    <li>Tente novamente em alguns instantes</li>
                    <li>Escolha outro método de pagamento</li>
                    <li>Verifique os dados informados</li>
                    <li>Entre em contato com o suporte se o problema persistir</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="payment-modal-footer">
              <button 
                onClick={() => setShowErrorModal(false)}
                className="btn-continue error"
              >
                <Icon icon="mdi:check" /> Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Customizado - Valor Mínimo para Boleto */}
      {showBoletoMinValueModal && (
        <div className="payment-modal-overlay" onClick={() => setShowBoletoMinValueModal(false)}>
          <div className="payment-modal error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header error">
              <Icon icon="mdi:alert-circle" className="error-icon-large" />
              <span style={{ fontSize: '20px', fontWeight: '600', display: 'block' }}>
                Valor Mínimo para Boleto
              </span>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowBoletoMinValueModal(false)}
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
            <div className="payment-modal-body">
              <div className="error-message-box">
                <p className="error-message-main">{boletoErrorMessage}</p>
                
                <div className="error-suggestions">
                  <p><strong>O que você pode fazer:</strong></p>
                  <ul>
                    <li>Escolha outro método de pagamento (PIX ou Cartão de Crédito)</li>
                    <li>Adicione mais itens ao carrinho para atingir o valor mínimo</li>
                    <li>Entre em contato com o suporte se tiver dúvidas</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="payment-modal-footer">
              <button 
                onClick={() => setShowBoletoMinValueModal(false)}
                className="btn-continue error"
                style={{ width: '100%', maxWidth: '100%' }}
              >
                <Icon icon="mdi:check" /> Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingCheckout;
